import { IStream } from "@sablier/models";
import { StreamCategory } from "@sablier/v2-constants";
import { framework } from "@sablier/v2-contracts";
import { _ } from "@sablier/v2-mixins";
import { vendors } from "@sablier/v2-utils";
import { gasser } from "~/client/utils";
import type { Event } from "@sablier/v2-contracts";
import type { IActor, IAddress, ITransactionReceipt } from "@sablier/v2-types";
import type { Abi } from "viem";

interface Extract {
  receipt: ITransactionReceipt;
  purpose: StreamCategory;
}

export async function extractIds({
  receipt,
  purpose,
}: Extract): Promise<string[] | undefined> {
  try {
    const name: Event<typeof purpose> =
      purpose === StreamCategory.LOCKUP_LINEAR
        ? "CreateLockupLinearStream"
        : "CreateLockupDynamicStream";

    const abis = (await import("@sablier/v2-contracts/abis")).default;
    const abi = abis[purpose] as Abi;

    return framework.delog({
      abi,
      log: name,
      receipt,
      search: "streamId",
    });
  } catch (error) {
    vendors.crash.log(error);
  }
  return undefined;
}

export async function extractId({
  receipt,
  purpose,
}: Extract): Promise<string | undefined> {
  const ids = await extractIds({ receipt, purpose });

  if (ids && ids.length) {
    return ids[0];
  }

  return undefined;
}

interface Configuration {
  chainId: number;
  query: Parameters<typeof framework.fuel>["0"]["query"];
  signer: Parameters<typeof framework.fuel>["0"]["signer"];
}

async function configure({ chainId, query, signer }: Configuration) {
  const fallback = gasser(chainId, query.purpose, query.method);
  return framework.fuel({ query, signer, fallback });
}

interface Identify {
  address: IAddress | undefined;
  proxy?: IAddress | undefined;
  stream: IStream;
}

function identify({ address, proxy, stream }: Identify): IActor {
  if (stream.proxied && _.toAddress(stream.sender) === _.toAddress(proxy)) {
    return "sender-proxy";
  }

  if (!stream.proxied && _.toAddress(stream.sender) === _.toAddress(address)) {
    return "sender-native";
  }

  if (_.toAddress(stream.recipient) === _.toAddress(address)) {
    return "recipient";
  }

  return undefined;
}

const helper = {
  extractId,
  extractIds,
  configure,
  identify,
};

export default helper;
